import React, { useMemo, useRef, useState } from 'react';
import { Button, message, Modal, Upload } from 'antd';
import { CameraOutlined, UploadOutlined } from '@ant-design/icons';
import { useParams } from 'react-router-dom';
import { useDocument } from '../../context/documentInfoContex';
import { templateService } from '../../api';
import { uploadToCloudinary } from '../../utils/cloudinary';
import {
  DEFAULT_COMPRESS_THRESHOLD_BYTES,
  uploadImageWithOptionalCompression,
} from '../../utils/imageUpload';
import CompressionConfirmModal from '../ImageCompressionModal/CompressionConfirmModal';
import './TemplateThumbnail.css';

const createDefaultThumbnail = () => {
  const canvas = document.createElement('canvas');
  canvas.width = 300;
  canvas.height = 200;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, 300, 200);
  ctx.fillStyle = '#cccccc';
  ctx.font = '24px Arial';
  ctx.textAlign = 'center';
  ctx.fillText('Default Thumbnail', 150, 100);
  return canvas.toDataURL('image/png');
};

const TemplateThumbnail = ({ viewMode, onMenuClick }) => {
  const { id } = useParams();
  const { isTemplateDocument, templateData, setTemplateData, setSavingData } = useDocument();
  const docID = id;
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [customUrl, setCustomUrl] = useState(null);
  const [isCapturing, setIsCapturing] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const [compressModalOpen, setCompressModalOpen] = useState(false);
  const [compressFileSize, setCompressFileSize] = useState(0);
  const compressChoiceRef = useRef(null);

  const existingThumbnail = useMemo(
    () => templateData?.thumbnail || createDefaultThumbnail(),
    [templateData?.thumbnail],
  );

  const handleOpenModal = () => {
    setPreviewUrl(null);
    setIsModalVisible(true);
  };

  const handleCapture = async () => {
    try {
      setIsCapturing(true);
      message.loading({ content: 'Capturing and uploading thumbnail...', key: 'capture' });

      const canvas = document.querySelector('.canvas-container canvas');
      if (!canvas) {
        message.error({ content: 'Canvas not found', key: 'capture' });
        return;
      }

      if (canvas.width === 0 || canvas.height === 0) {
        throw new Error('Canvas has no drawable content');
      }

      const thumbnailDataUrl = canvas.toDataURL('image/png', 0.85);
      const cloudinaryUrl = await uploadToCloudinary(thumbnailDataUrl);
      setPreviewUrl(cloudinaryUrl);
      message.success({ content: 'Thumbnail captured successfully!', key: 'capture' });
    } catch (error) {
      console.error('Failed to capture thumbnail:', error);
      message.error({ content: error?.message || 'Failed to capture thumbnail', key: 'capture' });
    } finally {
      setIsCapturing(false);
    }
  };

  const handleConfirmUpdate = async () => {
    try {
      const thumbnailUrl = customUrl || previewUrl || templateData?.thumbnail;

      if (!thumbnailUrl) {
        message.warning('Please capture or upload a thumbnail first');
        return;
      }

      const res = await templateService.updateTemplate({
        templateId: docID,
        data: { thumbnail: thumbnailUrl, docType: 'mileboard' },
        dispatch: setSavingData,
      });

      message.success('Thumbnail saved successfully');
      setIsModalVisible(false);
      setPreviewUrl(null);
      setCustomUrl(null);

      if (res?.data?.data) {
        setTemplateData(res.data.data);
      } else {
        setTemplateData((prev) => ({ ...(prev || {}), thumbnail: thumbnailUrl }));
      }
    } catch (error) {
      console.error('Failed to save thumbnail:', error);
      message.error('Failed to save thumbnail');
    }
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    setPreviewUrl(null);
    setCustomUrl(null);
  };

  const handleUpload = async (file) => {
    try {
      setIsUploading(true);

      if (file.size <= DEFAULT_COMPRESS_THRESHOLD_BYTES) {
        message.loading({ content: 'Uploading image...', key: 'upload' });
      }

      const cloudinaryUrl = await uploadImageWithOptionalCompression(file, {
        thresholdBytes: DEFAULT_COMPRESS_THRESHOLD_BYTES,
        promptCompress: async (largeFile) => {
          message.destroy('upload');
          return new Promise((resolve, reject) => {
            setCompressFileSize(largeFile.size);
            compressChoiceRef.current = { resolve, reject };
            setCompressModalOpen(true);
          });
        },
      });

      setCustomUrl(cloudinaryUrl);
      message.success({ content: 'Image uploaded successfully!', key: 'upload' });
    } catch (error) {
      if (error?.isCancel) {
        return;
      }
      console.error('Failed to upload image:', error);
      message.error({ content: error?.message || 'Failed to upload image', key: 'upload' });
    } finally {
      setIsUploading(false);
    }

    return false;
  };

  const closeCompressModal = (choice) => {
    setCompressModalOpen(false);
    const pending = compressChoiceRef.current;
    compressChoiceRef.current = null;
    if (!pending) return;

    if (choice === 'cancel') {
      pending.reject(Object.assign(new Error('cancelled'), { isCancel: true }));
      return;
    }

    message.loading({
      content:
        choice === 'compress'
          ? 'Compressing and uploading image...'
          : 'Uploading original image...',
      key: 'upload',
    });
    pending.resolve(choice === 'compress');
  };

  if (!isTemplateDocument) return null;

  const compressionModalZ = 2000;

  const modalContent = (
    <>
      <Modal
        title={(
          <div className="thumbnail-modal-header">
            <CameraOutlined className="thumbnail-modal-icon" />
            Thumbnail Preview
          </div>
        )}
        visible={isModalVisible}
        onCancel={handleCancel}
        className="thumbnail-modal"
        width={560}
        footer={[
          <Button key="cancel" onClick={handleCancel} className="cancel-btn">
            Cancel
          </Button>,
          <Button
            key="save"
            type="primary"
            onClick={handleConfirmUpdate}
            className="update-thumbnail-btn"
          >
            Save Thumbnail
          </Button>,
        ]}
      >
        <div className="thumbnail-content">
          <div className="thumbnail-preview-container" role="img" aria-label="thumbnail-preview">
            {(customUrl || previewUrl || existingThumbnail) ? (
              <img
                src={customUrl || previewUrl || existingThumbnail}
                alt="Thumbnail Preview"
                className="thumbnail-preview"
              />
            ) : (
              <div className="thumbnail-placeholder">
                <div className="placeholder-icon">📄</div>
                <p>No thumbnail yet</p>
              </div>
            )}
          </div>
          <div className="upload-section">
            <div className="upload-label">Upload a custom thumbnail</div>
            <Upload
              beforeUpload={handleUpload}
              showUploadList={false}
              accept="image/*"
              disabled={isUploading}
            >
              <Button
                icon={<UploadOutlined />}
                className="upload-btn"
                loading={isUploading}
                disabled={isUploading}
              >
                {isUploading ? 'Uploading...' : 'Upload Custom Thumbnail'}
              </Button>
            </Upload>
            <div className="capture-label">Capture current view</div>
            <Button
              icon={<CameraOutlined />}
              onClick={handleCapture}
              className="capture-btn"
              loading={isCapturing}
              disabled={isCapturing}
            >
              {isCapturing ? 'Capturing...' : 'Capture Current View'}
            </Button>
          </div>
        </div>
      </Modal>
      <CompressionConfirmModal
        open={compressModalOpen}
        fileSize={compressFileSize}
        onCompress={() => closeCompressModal('compress')}
        onUploadOriginal={() => closeCompressModal('original')}
        onCancel={() => closeCompressModal('cancel')}
        zIndex={compressionModalZ}
        getContainer={() => document.body}
      />
    </>
  );

  if (viewMode === 'menu') {
    return (
      <>
        <div
          className="menu-item"
          onClick={() => {
            handleOpenModal();
            if (onMenuClick) onMenuClick();
          }}
        >
          <CameraOutlined className="toggle-menu-icon" />
          <span>Update Thumbnail</span>
        </div>
        {modalContent}
      </>
    );
  }

  return (
    <>
      <Button
        type="text"
        icon={<CameraOutlined className="thumbnail-button-icon" />}
        onClick={handleOpenModal}
        title="Save Thumbnail"
        size="small"
        className="thumbnail-button"
      />
      {modalContent}
    </>
  );
};

export default TemplateThumbnail;
